import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  MenuItem,
} from "@mui/material";
import { Form, Field } from "react-final-form";
import { Autocomplete } from "mui-rff";
import styles from "./CreatePlaylistModal.module.scss";

type Option = {
  label: string;
  value: number | string;
  [key: string]: any;
};

interface CreatePlaylistModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: any) => void | Promise<void>;
  categories: any[];
  treatmentPlans: Option[];
  onTreatmentChange: (event: any, newValue: Option[]) => void;
}

const validate = (values: any) => {
  const errors: Record<string, string> = {};

  if (!values.playlistName?.trim()) {
    errors.playlistName = "Playlist name is required";
  }

  if (!values.category_id) {
    errors.category_id = "Category is required";
  }

  if (!values.treatment_idsnew || values.treatment_idsnew.length === 0) {
    errors.treatment_idsnew = "Select at least one treatment";
  }

  return errors;
};

const CreatePlaylistModal: React.FC<CreatePlaylistModalProps> = ({
  open,
  onClose,
  onSubmit,
  categories,
  treatmentPlans,
  onTreatmentChange,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ className: styles.createPlaylistDialog }}
    >
      <DialogTitle className={styles.dialogTitle}>
        Add New Playlist
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <Form
          onSubmit={onSubmit}
          validate={validate}
          render={({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit} className={styles.dialogForm}>
              <Box className={styles.headerBlock}>
                <h3>Create playlist</h3>
                <p>
                  Add a playlist name and link the treatments
                  that should appear under this playlist.
                </p>
              </Box>

              <Box className={styles.dialogFieldStack}>
                <Field name="playlistName">
                  {({ input, meta }) => (
                    <TextField
                      label="Playlist name"
                      placeholder="Enter playlist name"
                      variant="outlined"
                      fullWidth
                      size="small"
                      {...input}
                      autoFocus
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    />
                  )}
                </Field>

                <Field name="category_id">
                  {({ input, meta }) => (
                    <TextField
                      select
                      label="Category"
                      variant="outlined"
                      fullWidth
                      size="small"
                      {...input}
                      error={meta.touched && Boolean(meta.error)}
                      helperText={meta.touched && meta.error}
                    >
                      {categories?.map((category: any) => (
                        <MenuItem value={category.id} key={category.id}>
                          {category.category_name || category.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Field>

                <Autocomplete
                  name="treatment_idsnew"
                  label="Linked treatments"
                  multiple
                  required
                  options={treatmentPlans}
                  getOptionLabel={(option: Option) => option.label || ""}
                  isOptionEqualToValue={(option: Option, value: Option) =>
                    option.value === value.value
                  }
                  onChange={onTreatmentChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Linked treatments"
                      placeholder="Search and select treatments"
                      size="small"
                    />
                  )}
                />
              </Box>

              <DialogActions className={styles.dialogActions}>
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className={styles.cancelBtn}
                  disabled={submitting}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  className={styles.primaryBtn}
                  disabled={submitting}
                >
                  Add Playlist
                </Button>
              </DialogActions>
            </form>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

export default CreatePlaylistModal;